import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Tag } from "primereact/tag";
import { useContext, useMemo, useState } from "react";

import { ToastContext } from "../../../context/ToastContext";
import { parseDate } from "../../../helpers/date";
import {
  useDeleteOverrideItemMutation,
  useGetOverrideItemsQuery,
} from "../../../store/queries/baseRateFiles";
import { IBaseRateOverrideItem } from "../../../types/baseRateFile";
import {
  FEE_SCHEDULE_TYPE_LABELS,
  FEE_SCHEDULE_TYPE_OPTIONS,
  RULE_TYPE_LABELS,
  RULE_TYPE_OPTIONS,
  STATUS_OPTIONS,
} from "../utils/constants";
import AddOverrideRuleDialog from "./AddOverrideRuleDialog";

function ConfigsTable() {
  const toast = useContext(ToastContext);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editItem, setEditItem] = useState<IBaseRateOverrideItem | null>(null);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [itemToDelete, setItemToDelete] =
    useState<IBaseRateOverrideItem | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedRuleTypes, setSelectedRuleTypes] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<boolean | null>(null);

  const { data: overrideItemsData, isLoading } = useGetOverrideItemsQuery();
  const [deleteOverrideItem] = useDeleteOverrideItemMutation();
  const overrideItems = overrideItemsData?.data || [];

  // Filter items based on selected fee schedule types, rule types, and status
  const filteredItems = useMemo(() => {
    return overrideItems.filter((item) => {
      const matchesFeeScheduleType =
        !selectedTypes?.length ||
        (item.feeScheduleType && selectedTypes.includes(item.feeScheduleType));
      const matchesRuleType =
        !selectedRuleTypes?.length || selectedRuleTypes.includes(item.ruleType);
      const matchesStatus =
        selectedStatus == null || item.isEnabled === selectedStatus;
      return matchesFeeScheduleType && matchesRuleType && matchesStatus;
    });
  }, [overrideItems, selectedTypes, selectedRuleTypes, selectedStatus]);

  const handleOpenDeleteDialog = (item: IBaseRateOverrideItem) => {
    setItemToDelete(item);
    setDeleteDialogVisible(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogVisible(false);
    setItemToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      await deleteOverrideItem(itemToDelete.id).unwrap();
      toast?.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Override rule deleted successfully",
      });
      handleCloseDeleteDialog();
    } catch (error) {
      toast?.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete override rule",
      });
    }
  };

  const handleOpenAddDialog = () => {
    setEditItem(null);
    setDialogVisible(true);
  };

  const handleOpenEditDialog = (item: IBaseRateOverrideItem) => {
    setEditItem(item);
    setDialogVisible(true);
  };

  const handleCloseDialog = () => {
    setDialogVisible(false);
    setEditItem(null);
  };

  const enabledBodyTemplate = (rowData: IBaseRateOverrideItem) => {
    return (
      <Tag
        value={rowData.isEnabled ? "Enabled" : "Disabled"}
        severity={rowData.isEnabled ? "success" : "danger"}
      />
    );
  };

  const ruleTypeBodyTemplate = (rowData: IBaseRateOverrideItem) => {
    return RULE_TYPE_LABELS[rowData.ruleType] || rowData.ruleType;
  };

  const feeScheduleTypeBodyTemplate = (rowData: IBaseRateOverrideItem) => {
    if (!rowData.feeScheduleType) return "-";
    return (
      FEE_SCHEDULE_TYPE_LABELS[rowData.feeScheduleType] ||
      rowData.feeScheduleType
    );
  };

  const ruleValueBodyTemplate = (rowData: IBaseRateOverrideItem) => {
    switch (rowData.ruleType) {
      case "FIXED_RATE":
        return rowData.overrideRate ? `$${rowData.overrideRate}` : "-";
      case "SOURCE_OVERRIDE":
        return rowData.sourceOverride || "-";
      case "SWAP":
        return rowData.swapWithHcpcs || "-";
      default:
        return "-";
    }
  };

  const modBodyTemplate = (rowData: IBaseRateOverrideItem) => {
    return rowData.mod || "-";
  };

  const createdAtBodyTemplate = (rowData: IBaseRateOverrideItem) => {
    return parseDate(rowData.createdAt);
  };

  const actionsBodyTemplate = (rowData: IBaseRateOverrideItem) => {
    return (
      <div className="flex gap-1">
        <Button
          icon="pi pi-pencil"
          className="p-button-text p-button-sm"
          tooltip="Edit"
          tooltipOptions={{ position: "top" }}
          onClick={() => handleOpenEditDialog(rowData)}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-text p-button-sm p-button-danger"
          tooltip="Delete"
          tooltipOptions={{ position: "top" }}
          onClick={() => handleOpenDeleteDialog(rowData)}
        />
      </div>
    );
  };

  return (
    <div>
      <div className="flex justify-content-between align-items-center mb-2">
        <div className="flex gap-2">
          <MultiSelect
            value={selectedTypes}
            options={FEE_SCHEDULE_TYPE_OPTIONS}
            onChange={(e) => setSelectedTypes(e.value)}
            placeholder="Fee Schedule Type"
            maxSelectedLabels={2}
            style={{ minWidth: "15rem" }}
            showClear
          />
          <MultiSelect
            value={selectedRuleTypes}
            options={RULE_TYPE_OPTIONS}
            onChange={(e) => setSelectedRuleTypes(e.value)}
            placeholder="Rule Type"
            maxSelectedLabels={2}
            style={{ minWidth: "12rem" }}
            showClear
          />
          <Dropdown
            value={selectedStatus}
            options={STATUS_OPTIONS}
            onChange={(e) => setSelectedStatus(e.value)}
            placeholder="Status"
            style={{ minWidth: "10rem" }}
            showClear
          />
        </div>
        <Button
          label="Add Rule"
          icon="pi pi-plus"
          onClick={handleOpenAddDialog}
        />
      </div>
      <DataTable
        value={filteredItems}
        loading={isLoading}
        paginator
        rows={10}
        emptyMessage="No override items configured."
        className="p-datatable-striped"
        sortField="priority"
        sortOrder={1}
      >
        <Column field="name" header="Name" sortable />
        <Column field="hcpcs" header="HCPCS" sortable />
        <Column field="mod" header="Modifier" body={modBodyTemplate} />
        <Column
          field="feeScheduleType"
          header="Fee Schedule Type"
          body={feeScheduleTypeBodyTemplate}
          sortable
        />
        <Column
          field="ruleType"
          header="Rule Type"
          body={ruleTypeBodyTemplate}
          sortable
        />
        <Column header="Rule Value" body={ruleValueBodyTemplate} />
        <Column field="priority" header="Priority" sortable />
        <Column
          field="isEnabled"
          header="Status"
          body={enabledBodyTemplate}
          sortable
        />
        <Column
          field="createdAt"
          header="Created"
          body={createdAtBodyTemplate}
          sortable
        />
        <Column header="Actions" body={actionsBodyTemplate} />
      </DataTable>

      <AddOverrideRuleDialog
        visible={dialogVisible}
        onHide={handleCloseDialog}
        editItem={editItem}
      />

      <ConfirmDialog
        visible={deleteDialogVisible}
        onHide={handleCloseDeleteDialog}
        message={`Are you sure you want to delete ${itemToDelete?.name}?`}
        header="Confirm Delete"
        icon="pi pi-exclamation-triangle"
        accept={handleConfirmDelete}
        reject={handleCloseDeleteDialog}
        acceptLabel="OK"
        rejectLabel="Cancel"
        draggable={false}
      />
    </div>
  );
}

export default ConfigsTable;
